import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SearchBar from "./SearchBar";
import CartDrawer from '../Layout/CartDrawer';
import OfferBar from '../../offers/OfferBar';
import { ShoppingBag, User, Menu, X, ChevronDown } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useActiveOffer } from '../../offers/useOffer';

// Original Cloudinary URL — served directly from CDN, no CORS issues
const LOGO_SRC = 'https://res.cloudinary.com/dmrlotfsv/video/upload/v1775402055/Logo_Reveal_wg1t0d.mp4';

// ─── Font ─────────────────────────────────────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('veshh-font')) {
  const link = document.createElement('link');
  link.id = 'veshh-font'; link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Exo+2:wght@300;400;500;600;700&display=swap';
  document.head.appendChild(link);
}

const BRAND_FONT = { fontFamily: "'Exo 2', sans-serif", fontWeight: 400 };

// ─── Video Logo ───────────────────────────────────────────────────────────────
const VideoLogo = ({ height = 80 }) => (
  <div style={{ height, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
    <video
      autoPlay
      muted
      loop
      playsInline
      disablePictureInPicture
      disableRemotePlayback
      style={{
        height: '100%',
        width: 'auto',
        display: 'block',
        pointerEvents: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <source src={LOGO_SRC} type="video/mp4" />
    </video>
  </div>
);

// ─── Desktop NavLink ──────────────────────────────────────────────────────────
const NavLink = ({ to, children }) => {
  const location = useLocation();
  const isActive = location.pathname === to ||
    (to !== '/' && location.pathname.startsWith(to));

  return (
    <Link to={to} className="nb-navlink" style={{
      ...BRAND_FONT,
      position: 'relative',
      fontSize: '11px', letterSpacing: '0.22em',
      textTransform: 'uppercase', textDecoration: 'none',
      color: isActive ? '#000' : 'black',
      fontWeight: isActive ? 500 : 400,
      paddingBottom: '3px',
    }}>
      {children}
      {isActive && (
        <span style={{
          position: 'absolute', bottom: 0, left: 0,
          width: '100%', height: '1px', background: '#000',
        }} />
      )}
    </Link>
  );
};

// ─── Cart Button ──────────────────────────────────────────────────────────────
const CartButton = ({ size = 21, activeIcon, onToggle, cartItemCount }) => (
  <button onClick={onToggle} style={{
    position: 'relative', background: 'transparent', border: 'none',
    cursor: 'pointer', display: 'flex', padding: 0,
    color: activeIcon === 'cart' ? '#000' : 'black',
  }}>
    <ShoppingBag size={size} strokeWidth={1.75} fill={activeIcon === 'cart' ? 'black' : 'none'} />
    {cartItemCount > 0 && (
      <span style={{
        position: 'absolute', top: '-5px', right: '-5px',
        background: '#000', color: '#fff',
        fontSize: '7px', fontFamily: "'Exo 2',sans-serif", fontWeight: 500,
        borderRadius: '50%', minWidth: '13px', height: '13px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 2px', lineHeight: 1,
      }}>
        {cartItemCount}
      </span>
    )}
  </button>
);

// ─── Navbar ───────────────────────────────────────────────────────────────────
const Navbar = () => {
  const [drawerOpen,           setDrawerOpen]           = useState(false);
  const [navDrawerOpen,        setNavDrawerOpen]        = useState(false);
  const [winterWearOpen,       setWinterWearOpen]       = useState(false);
  const [mobileWinterWearOpen, setMobileWinterWearOpen] = useState(false);
  const [activeIcon,           setActiveIcon]           = useState('');
  const [scrolled,             setScrolled]             = useState(false);

  const location      = useLocation();
  const { cart }      = useSelector((state) => state.cart);
  const { user }      = useSelector((state) => state.auth);
  const cartItemCount = cart?.products?.reduce((t, p) => t + p.quantity, 0) || 0;

  const toggleNavDrawer  = () => { setNavDrawerOpen(p => !p); setActiveIcon(p => p === 'menu' ? '' : 'menu'); };
  const toggleCartDrawer = () => { setDrawerOpen(p => !p);    setActiveIcon(p => p === 'cart' ? '' : 'cart'); };

  useEffect(() => { setNavDrawerOpen(false); setActiveIcon(''); }, [location.pathname]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const winterPaths = [
    '/collections/SweatShirt', '/collections/OverSized SweatShirt',
    '/collections/Hoodie', '/collections/Zip Hoodie',
    '/collections/OverSized Hoodie', '/collections/Acid Washed Hoodie',
    '/collections/Varsity Jacket',
  ];
  const winterActive = winterPaths.includes(location.pathname);

  const { offer: bogoOffer } = useActiveOffer();
  
  return (
    <>
      <style>{`
        @keyframes nb-fade { from { opacity: 0; } to { opacity: 1; } }
        .nb-navlink:hover { color: #000 !important; }
        .nb-dd-item:hover { background: #000 !important; color: #fff !important; }
        .nb-btn { background: transparent; border: none; cursor: pointer; display: flex; align-items: center; padding: 0; }
      `}</style>

      {/* ── STICKY WRAPPER ── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 40,
        width: '100%', backgroundColor: '#f8f8f8',
        borderBottom: `1px solid ${scrolled ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.07)'}`,
        boxShadow: scrolled ? '0 1px 16px rgba(0,0,0,0.04)' : 'none',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      }}>

        {/* ══ OFFER BAR (renders only while an offer is live) ══ */}
        <OfferBar />

        {/* ══ DESKTOP ══ */}
        <nav className="hidden md:grid" style={{
          maxWidth: '1400px', margin: '0 auto', padding: '0 48px',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center', height: '72px',
        }}>
          {/* LEFT — video logo */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Link to="/" draggable={false} style={{ display: 'flex', alignItems: 'center' }}>
              <VideoLogo height={60} />
            </Link>
          </div>

          {/* CENTER — links */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '28px' }}>
            
            <NavLink to="/collections/all">New Launches</NavLink>
            {bogoOffer && <NavLink to="/bogo">BOGO</NavLink>}
            <NavLink to="/collections/OverSized T-Shirt">Oversized Tee</NavLink>
            <NavLink to="/collections/Tie Dye Oversized T-Shirt">Tie Dye</NavLink>
            <NavLink to="/collections/Acid Washed OverSized T-Shirt">Acid Washed</NavLink>
            <NavLink to="/collections/Essentials">Essentials</NavLink>

            {/* Winter Wear dropdown */}
            <div style={{ position: 'relative' }}
              onMouseEnter={() => setWinterWearOpen(true)}
              onMouseLeave={() => setWinterWearOpen(false)}
            >
              <button className="nb-btn" style={{
                ...BRAND_FONT, fontSize: '11px', letterSpacing: '0.22em',
                textTransform: 'uppercase', gap: '4px',
                color: winterActive ? '#000' : 'black',
                fontWeight: winterActive ? 500 : 400,
              }}>
                Winter Wear
                <ChevronDown style={{
                  width: '11px', height: '11px',
                  transition: 'transform 0.2s ease',
                  transform: winterWearOpen ? 'rotate(180deg)' : 'rotate(0)',
                }} />
              </button>

              <div style={{
                position: 'absolute', left: 0, top: '100%', marginTop: '10px',
                width: '196px', backgroundColor: '#f8f8f8',
                border: '1px solid black', zIndex: 50,
                opacity: winterWearOpen ? 1 : 0,
                visibility: winterWearOpen ? 'visible' : 'hidden',
                transform: winterWearOpen ? 'translateY(0)' : 'translateY(-4px)',
                transition: 'opacity 0.18s ease, transform 0.18s ease, visibility 0.18s',
              }}>
                {[
                  ['SweatShirt',           '/collections/SweatShirt'],
                  ['Oversized SweatShirt', '/collections/OverSized SweatShirt'],
                  ['Hoodie',               '/collections/Hoodie'],
                  ['Zip Hoodie',           '/collections/Zip Hoodie'],
                  ['Oversized Hoodie',     '/collections/OverSized Hoodie'],
                  ['Acid Washed Hoodie',   '/collections/Acid Washed Hoodie'],
                  ['Varsity Jacket',       '/collections/Varsity Jacket'],
                ].map(([label, path], i) => (
                  <Link key={path} to={path} onClick={() => setWinterWearOpen(false)}
                    className="nb-dd-item"
                    style={{
                      ...BRAND_FONT, display: 'block', padding: '10px 16px',
                      fontSize: '10px', letterSpacing: '0.18em',
                      textTransform: 'uppercase', textDecoration: 'none',
                      color: location.pathname === path ? '#000' : 'rgba(0,0,0,0.45)',
                      fontWeight: location.pathname === path ? 500 : 400,
                      borderTop: i === 0 ? 'none' : '1px solid rgba(0,0,0,0.05)',
                      transition: 'background 0.12s ease, color 0.12s ease',
                    }}>
                    {label}
                  </Link>
                ))}
              </div>
            </div>

            <NavLink to="/contact">Contact</NavLink>
            <NavLink to="/about">About</NavLink>
            {!user && <NavLink to="/login">Login</NavLink>}
          </div>

          {/* RIGHT — icons */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '16px' }}>
            {user?.role === 'admin' && (
              <Link to="/admin" style={{
                ...BRAND_FONT, fontSize: '9px', letterSpacing: '0.3em',
                textTransform: 'uppercase', textDecoration: 'none',
                color: '#000', border: '1px solid black',
                padding: '5px 10px', transition: 'all 0.2s ease',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = '#000'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#000'; }}
              >Admin</Link>
            )}
            <Link to="/profile" style={{ display: 'flex', color: location.pathname === '/profile' ? '#000' : 'black' }}>
              <User size={21} strokeWidth={1.75} fill={location.pathname === '/profile' ? 'black' : 'none'} />
            </Link>
            <CartButton size={21} activeIcon={activeIcon} onToggle={toggleCartDrawer} cartItemCount={cartItemCount} />
            <div style={{ color: 'black', display: 'flex' }}>
              <SearchBar />
            </div>
          </div>
        </nav>

        {/* ══ MOBILE ══ */}
        <div className="flex md:hidden" style={{
          padding: '0 14px', height: '56px',
          alignItems: 'center', justifyContent: 'space-between',
          position: 'relative',
        }}>
          {/* Left — hamburger */}
          <button className="nb-btn" onClick={toggleNavDrawer}
            style={{ color: navDrawerOpen ? '#000' : 'black', zIndex: 1 }}>
            {navDrawerOpen
              ? <X size={20} strokeWidth={1.75} />
              : <Menu size={20} strokeWidth={1.75} />
            }
          </button>

          {/* Center — logo absolutely centered */}
          <div style={{
            position: 'absolute', left: '50%', top: '50%',
            transform: 'translate(-50%, -50%)',
          }}>
            <Link to="/" draggable={false} style={{ display: 'flex' }}>
              <VideoLogo height={38} />
            </Link>
          </div>

          {/* Right — search + user + cart */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '13px', zIndex: 1 }}>
            <div style={{ color: 'black', display: 'flex' }}>
              <SearchBar />
            </div>
            {user ? (
              <Link to="/profile" style={{ display: 'flex', color: location.pathname === '/profile' ? '#000' : 'rgba(0,0,0,0.45)' }}>
                <User size={19} strokeWidth={1.75} fill={location.pathname === '/profile' ? 'black' : 'none'} />
              </Link>
            ) : (
              <Link to="/login" style={{
                ...BRAND_FONT, fontSize: '8px', letterSpacing: '0.28em',
                textTransform: 'uppercase', textDecoration: 'none',
                color: 'black',
              }}>
                Login
              </Link>
            )}
            <CartButton size={19} activeIcon={activeIcon} onToggle={toggleCartDrawer} cartItemCount={cartItemCount} />
          </div>
        </div>
<div className="festival-garland" aria-hidden="true" />
      </div>

      <CartDrawer drawerOpen={drawerOpen} toggleCartDrawer={toggleCartDrawer} />

      {/* ══ MOBILE DRAWER ══ */}
      <div style={{
        position: 'fixed', top: 0, left: 0,
        width: '72%', maxWidth: '290px', height: '100%',
        backgroundColor: '#f8f8f8',
        borderRight: '1px solid black',
        transform: navDrawerOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.28s cubic-bezier(0.25,0.46,0.45,0.94)',
        zIndex: 50, overflowY: 'auto',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 20px', borderBottom: '1px solid black', flexShrink: 0,
        }}>
          <span style={{ ...BRAND_FONT, fontSize: '8px', letterSpacing: '0.45em', textTransform: 'uppercase', color: 'black' }}>
            Navigation
          </span>
          <button className="nb-btn" onClick={toggleNavDrawer} style={{ color: 'black' }}>
            <X size={16} strokeWidth={1.75} />
          </button>
        </div>

        <nav style={{ padding: '8px 20px 24px', display: 'flex', flexDirection: 'column', flex: 1 }}>
          {[
            ...(bogoOffer ? [['BOGO', '/bogo']] : []),
            ['New Launches',        '/collections/all'],
            ['Oversized T-Shirt',   '/collections/OverSized T-Shirt'],
            ['Tie Dye',             '/collections/Tie Dye Oversized T-Shirt'],
            ['Acid Washed T-Shirt', '/collections/Acid Washed OverSized T-Shirt'],
            ['Essentials',          '/collections/Essentials'],
          ].map(([label, path]) => (
            <Link key={path} to={path} onClick={toggleNavDrawer} style={{
              ...BRAND_FONT, display: 'block', padding: '13px 0',
              fontSize: '11px', letterSpacing: '0.25em',
              textTransform: 'uppercase', textDecoration: 'none',
              color: location.pathname === path ? '#000' : 'black',
              fontWeight: location.pathname === path ? 500 : 400,
              borderBottom: '1px solid black',
            }}>
              {label}
            </Link>
          ))}

          {/* Winter Wear accordion */}
          <div style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <button onClick={() => setMobileWinterWearOpen(p => !p)} className="nb-btn" style={{
              ...BRAND_FONT, width: '100%', justifyContent: 'space-between',
              padding: '13px 0', fontSize: '11px', letterSpacing: '0.25em',
              textTransform: 'uppercase',
              color: winterActive ? '#000' : 'black',
              fontWeight: winterActive ? 500 : 400,
            }}>
              <span>Winter Wear</span>
              <ChevronDown style={{
                width: '12px', height: '12px',
                transition: 'transform 0.2s ease',
                transform: mobileWinterWearOpen ? 'rotate(180deg)' : 'rotate(0)',
                color: 'black',
              }} />
            </button>

            {mobileWinterWearOpen && (
              <div style={{ paddingBottom: '6px', paddingLeft: '10px' }}>
                {[
                  ['SweatShirt',           '/collections/SweatShirt'],
                  ['Oversized SweatShirt', '/collections/OverSized SweatShirt'],
                  ['Hoodie',               '/collections/Hoodie'],
                  ['Zip Hoodie',           '/collections/Zip Hoodie'],
                  ['Oversized Hoodie',     '/collections/OverSized Hoodie'],
                  ['Acid Washed Hoodie',   '/collections/Acid Washed Hoodie'],
                  ['Varsity Jacket',       '/collections/Varsity Jacket'],
                ].map(([label, path]) => (
                  <Link key={path} to={path} onClick={toggleNavDrawer} style={{
                    ...BRAND_FONT, display: 'block', padding: '9px 0',
                    fontSize: '10px', letterSpacing: '0.2em',
                    textTransform: 'uppercase', textDecoration: 'none',
                    color: location.pathname === path ? '#000' : 'black',
                    fontWeight: location.pathname === path ? 500 : 400,
                  }}>
                    {label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {[['Contact', '/contact'], ['About', '/about']].map(([label, path]) => (
            <Link key={path} to={path} onClick={toggleNavDrawer} style={{
              ...BRAND_FONT, display: 'block', padding: '13px 0',
              fontSize: '11px', letterSpacing: '0.25em',
              textTransform: 'uppercase', textDecoration: 'none',
              color: location.pathname === path ? '#000' : 'black',
              fontWeight: location.pathname === path ? 500 : 400,
              borderBottom: '1px solid black',
            }}>
              {label}
            </Link>
          ))}

          {user?.role === 'admin' && (
            <Link to="/admin" onClick={toggleNavDrawer} style={{
              ...BRAND_FONT, display: 'block', padding: '13px 0',
              fontSize: '11px', letterSpacing: '0.25em',
              textTransform: 'uppercase', textDecoration: 'none',
              color: 'black',
              borderBottom: '1px solid black',
            }}>
              Admin
            </Link>
          )}
        </nav>

        <div style={{ padding: '16px 20px', borderTop: '1px solid black', flexShrink: 0 }}>
          <span style={{ ...BRAND_FONT, fontSize: '8px', letterSpacing: '0.35em', textTransform: 'uppercase', color: 'black' }}>
            VESHH-EON
          </span>
        </div>
      </div>

      {/* Backdrop */}
      {navDrawerOpen && (
        <div onClick={toggleNavDrawer} style={{
          position: 'fixed', inset: 0,
          background: 'black',
          backdropFilter: 'blur(1px)',
          zIndex: 40, animation: 'nb-fade 0.22s ease',
        }} />
      )}
    </>
  );
};

export default Navbar;
